/**
 * Sole owner of the SWADE die-formula grammar. Parses a formula string into terms, renders terms
 * back to a display string, and computes numeric bounds when every term is evaluable.
 *
 * A formula is terms joined by `+` or `-`. Whitespace is optional, `x`, `*` and `×` are all
 * accepted as multiply, and parentheses are presentational only. Anything that is neither dice
 * notation nor a bare number becomes an opaque token, so `Smarts x2` displays without being
 * numerically evaluable.
 */
export type DieTerm =
    | { kind: 'dice'; sign: 1 | -1; count: number; sides: number; multiplier: number }
    | { kind: 'flat'; sign: 1 | -1; value: number }
    | { kind: 'token'; sign: 1 | -1; text: string; multiplier: number };

const MULTIPLIER_PATTERN: RegExp = /[x*×]\s*(\d+)\s*$/i;
const DICE_PATTERN: RegExp = /^(\d+)?d(\d+)$/i;
const FLAT_PATTERN: RegExp = /^\d+$/;

function parseTerm(segment: string, sign: 1 | -1): DieTerm {
    let text = segment.trim();
    let multiplier = 1;

    const scaled = MULTIPLIER_PATTERN.exec(text);
    if (scaled) {
        multiplier = parseInt(scaled[1], 10);
        text = text.slice(0, scaled.index).trim();
    }

    const dice = DICE_PATTERN.exec(text);
    if (dice) {
        return {
            kind: 'dice',
            sign,
            count: dice[1] ? parseInt(dice[1], 10) : 1,
            sides: parseInt(dice[2], 10),
            multiplier,
        };
    }

    if (FLAT_PATTERN.test(text)) {
        // A bare number carries no multiplier of its own; fold it into the value instead.
        return { kind: 'flat', sign, value: parseInt(text, 10) * multiplier };
    }

    return { kind: 'token', sign, text, multiplier };
}

export function parseTerms(value: string): DieTerm[] {
    // Parentheses are presentational — the renderer re-adds them around a multiplied leading token.
    const raw = (value ?? '').trim().replace(/[()]/g, '');
    if (raw === '') return [];

    const terms: DieTerm[] = [];
    let sign: 1 | -1 = 1;

    for (const part of raw.split(/([+\-])/).map((piece) => piece.trim())) {
        if (part === '') continue;
        // Signs in a run compose rather than overwrite: `--` cancels to `+`, `-+` stays `-`.
        // Splitting on the operator yields an empty segment between consecutive signs, so
        // without this a run would silently keep only its last sign.
        if (part === '+' || part === '-') {
            if (part === '-') sign = sign === 1 ? -1 : 1;
            continue;
        }
        terms.push(parseTerm(part, sign));
        sign = 1;
    }

    return terms;
}

function renderTerm(term: DieTerm): string {
    if (term.kind === 'flat') return String(term.value);

    if (term.kind === 'dice') {
        const base = `${term.count > 1 ? term.count : ''}d${term.sides}`;
        return term.multiplier !== 1 ? `${base}x${term.multiplier}` : base;
    }

    return term.multiplier !== 1 ? `${term.text} x${term.multiplier}` : term.text;
}

export function renderTerms(terms: DieTerm[]): string {
    return terms
        .map((term, index) => {
            let text = renderTerm(term);

            // A multiplied leading token needs parentheses so the multiplier's scope stays clear:
            // `(Spirit x2)+d6`. Dice multipliers such as `3d4x10` are unambiguous without them.
            if (index === 0 && terms.length > 1 && term.kind === 'token' && term.multiplier !== 1) {
                text = `(${text})`;
            }

            if (index === 0) return term.sign === -1 ? `-${text}` : text;
            return `${term.sign === -1 ? '-' : '+'}${text}`;
        })
        .join('');
}

function termBounds(term: DieTerm): [number, number] {
    if (term.kind === 'flat') return [term.value, term.value];
    if (term.kind === 'dice') {
        return [term.count * term.multiplier, term.count * term.sides * term.multiplier];
    }
    return [0, 0];
}

export default class DieCode {
    private terms: DieTerm[] = [];
    private normalized: string = '';

    constructor(value: string) {
        this.terms = parseTerms(value);
        this.normalized = (value ?? '').trim().toLowerCase();
    }

    public static fromTerms(terms: DieTerm[]): DieCode {
        return new DieCode(renderTerms(terms));
    }

    public getTerms(): DieTerm[] {
        return this.terms.map((term) => ({ ...term }));
    }

    /** True only when every term is numerically evaluable — no opaque tokens. */
    public isParsed(): boolean {
        return this.terms.length > 0 && this.terms.every((term) => term.kind !== 'token');
    }

    public getNotation(): string | null {
        return this.isParsed() ? this.normalized : null;
    }

    public getFormula(): string {
        return renderTerms(this.terms);
    }

    public toString(): string {
        return this.getFormula();
    }

    public getMin(): number {
        if (!this.isParsed()) return 0;
        return this.terms.reduce((total, term) => {
            const [low, high] = termBounds(term);
            return total + (term.sign === 1 ? low : -high);
        }, 0);
    }

    public getMax(): number {
        if (!this.isParsed()) return 0;
        return this.terms.reduce((total, term) => {
            const [low, high] = termBounds(term);
            return total + (term.sign === 1 ? high : -low);
        }, 0);
    }

    public roll(): number {
        if (!this.isParsed()) return 0;
        return this.terms.reduce((total, term) => {
            if (term.kind === 'flat') return total + (term.sign * term.value);
            if (term.kind === 'dice') {
                let sum = 0;
                for (let i = 0; i < term.count; i++) {
                    sum += Math.floor(Math.random() * term.sides) + 1;
                }
                return total + (term.sign * sum * term.multiplier);
            }
            return total;
        }, 0);
    }
}
